import { useState } from "react";
import { Lock, Tv } from "lucide-react";

// The "in premieră" gate password. This is not a security control — it just
// keeps casual visitors out during pre-launch. If needed later, move the
// verification server-side.
const PREVIEW_PASSWORD = "DESENELECOPILARIEI";
const STORAGE_KEY = "desene2000_preview_access";

export function hasPreviewAccess() {
  try {
    return typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function PreviewGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (value.trim().toUpperCase() === PREVIEW_PASSWORD) {
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore quota */ }
      setError(false);
      onUnlock?.();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div
      data-testid="preview-gate"
      className="fixed inset-0 z-[9999] bg-[#05070a] flex items-center justify-center p-6 overflow-hidden"
    >
      {/* Ambient neon glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: "#ff2fa7" }} />
        <div className="absolute -bottom-24 -right-16 w-[28rem] h-[28rem] rounded-full opacity-20 blur-3xl" style={{ background: "#00e5ff" }} />
        {/* Scanlines */}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{
            background:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
          }}
        />
      </div>

      <form
        onSubmit={submit}
        className={`relative w-full max-w-md bg-[#0a0f0d]/90 border-4 border-[#ff2fa7] rounded-2xl p-7 sm:p-9 backdrop-blur-sm ${shake ? "animate-shake" : ""}`}
        style={{ boxShadow: "0 0 24px rgba(255,47,167,0.35), 0 0 60px rgba(0,229,255,0.15)" }}
      >
        {/* Brand badge */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#ff2fa7]"
            style={{ boxShadow: "0 0 12px rgba(255,47,167,0.9)" }}
          >
            <Tv className="w-4 h-4 text-white" strokeWidth={3} />
          </span>
          <span
            className="font-display font-black tracking-tight text-2xl"
            style={{
              background: "linear-gradient(90deg,#ff2fa7,#00e5ff)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            DESENE2000
          </span>
        </div>

        <div className="flex items-center gap-2 mb-2 text-[#00e5ff]">
          <Lock className="w-4 h-4" strokeWidth={3} />
          <span className="font-pixel text-xs uppercase tracking-[0.25em]">Acces în premieră</span>
        </div>

        <h1 className="font-display text-2xl sm:text-3xl font-black text-white leading-tight mb-2">
          Pentru acces în premieră<br />introduceți parola
        </h1>
        <p className="text-slate-400 text-sm mb-6">
          Site-ul este momentan disponibil doar cu invitație. Introduceți parola primită pentru a continua.
        </p>

        <label htmlFor="preview-password" className="sr-only">Parolă</label>
        <input
          id="preview-password"
          data-testid="preview-gate-input"
          type="password"
          autoFocus
          value={value}
          onChange={(e) => { setValue(e.target.value); if (error) setError(false); }}
          placeholder="•••••••••••••••"
          className={`w-full px-4 py-3 rounded-xl bg-[#05070a] border-2 font-pixel text-lg tracking-[0.35em] text-center text-[#00e5ff] placeholder:text-[#00e5ff]/30 outline-none transition-colors ${
            error ? "border-red-500" : "border-[#ff2fa7]/50 focus:border-[#ff2fa7]"
          }`}
          style={{ textShadow: "0 0 6px rgba(0,229,255,0.55)" }}
        />

        {error && (
          <div className="mt-3 text-center text-red-400 text-xs font-black uppercase tracking-widest" data-testid="preview-gate-error">
            Parolă incorectă
          </div>
        )}

        <button
          type="submit"
          data-testid="preview-gate-submit"
          className="mt-6 w-full px-4 py-3 rounded-xl bg-[#ff2fa7] text-white border-2 border-[#ff2fa7] font-black uppercase tracking-[0.2em] text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform"
          style={{ boxShadow: "0 0 18px rgba(255,47,167,0.7)" }}
        >
          Intră în emisie
        </button>

        <p className="mt-6 text-center text-[10px] font-black uppercase tracking-widest text-slate-500">
          © Desene 2000 · Ediție privată
        </p>
      </form>

      <style>{`
        @keyframes desene-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-shake { animation: desene-shake 0.5s ease-in-out; }
      `}</style>
    </div>
  );
}
